import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Card, CardBody, Badge, Button, Input } from '@img/ui';
import { api, type Paginated } from '../lib/api';
import { SAUDI_CITIES } from '../lib/saudi-locations';
import { SEO } from '../components/SEO';

interface PropMapItem {
  id: string;
  title: string;
  city: string;
  district: string | null;
  type: string;
  purpose: string;
  price: number | string;
  area: number;
  rooms: number;
  bathrooms: number;
  coverImage: string | null;
  latitude: number | string | null;
  longitude: number | string | null;
}

// 🧠 typings مبسّطة لـ Leaflet (CDN)
interface LBounds {
  getNorth: () => number;
  getSouth: () => number;
  getEast: () => number;
  getWest: () => number;
  contains: (latlng: [number, number]) => boolean;
}
interface LMap {
  setView: (latlng: [number, number], zoom: number) => LMap;
  remove: () => void;
  removeLayer: (layer: unknown) => void;
  getZoom: () => number;
  on: (evt: string, fn: () => void) => void;
  getBounds: () => LBounds;
}
interface LeafletGlobal {
  map: (el: HTMLElement) => LMap;
  marker: (latlng: [number, number], opts?: object) => {
    addTo: (m: LMap) => unknown;
    bindPopup: (html: string) => unknown;
    on: (evt: string, fn: () => void) => void;
  };
  divIcon: (opts: { html: string; className: string; iconSize: [number, number] }) => unknown;
  tileLayer: (url: string, opts: object) => { addTo: (m: LMap) => unknown };
  Icon: { Default: { prototype: object; mergeOptions: (o: object) => void } };
}
declare global {
  interface Window {
    L?: LeafletGlobal;
    __leafletLoading__?: Promise<LeafletGlobal>;
  }
}

const LEAFLET_JS = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
const LEAFLET_CSS = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';

function loadLeaflet(): Promise<LeafletGlobal> {
  if (window.L) return Promise.resolve(window.L);
  if (window.__leafletLoading__) return window.__leafletLoading__;
  window.__leafletLoading__ = new Promise<LeafletGlobal>((resolve, reject) => {
    if (!document.querySelector(`link[href="${LEAFLET_CSS}"]`)) {
      const link = document.createElement('link');
      link.rel = 'stylesheet'; link.href = LEAFLET_CSS;
      document.head.appendChild(link);
    }
    const script = document.createElement('script');
    script.src = LEAFLET_JS; script.async = true;
    script.onload = () => window.L ? resolve(window.L) : reject(new Error('L undefined'));
    script.onerror = () => reject(new Error('CDN error'));
    document.head.appendChild(script);
  });
  return window.__leafletLoading__;
}

const CITY_CENTERS: Record<string, [number, number]> = {
  'الرياض':         [24.7136, 46.6753],
  'جدة':            [21.4858, 39.1925],
  'مكة المكرمة':    [21.3891, 39.8579],
  'المدينة المنورة':[24.5247, 39.5692],
  'الدمام':         [26.4207, 50.0888],
  'الخبر':          [26.2172, 50.1971],
  'الطائف':         [21.2703, 40.4158],
  'تبوك':           [28.3838, 36.5550],
  'بريدة':          [26.3260, 43.9750],
  'حائل':           [27.5219, 41.6907],
  'أبها':           [18.2164, 42.5053],
  'خميس مشيط':      [18.3060, 42.7264],
  'نجران':          [17.4924, 44.1277],
  'جازان':          [16.8892, 42.5511],
  'الباحة':         [20.0129, 41.4677],
  'سكاكا':          [29.9698, 40.2064],
  'عرعر':           [30.9753, 41.0381],
  'ينبع':           [24.0883, 38.0617],
};

// 🏠 رموز أنواع العقارات
const TYPE_EMOJI: Record<string, string> = {
  VILLA: '🏡', APARTMENT: '🏢', LAND: '🟫', COMMERCIAL: '🏬',
  OFFICE: '🏢', BUILDING: '🏗️', FARM: '🌾', WAREHOUSE: '📦', REST_HOUSE: '🏖️',
};

export function PropertiesMapPage() {
  const [filters, setFilters] = useState({
    city: 'الرياض',
    purpose: '',
    type: '',
    maxPrice: '',
    minPrice: '',
    minRooms: '',
    minArea: '',
    maxArea: '',
    q: '',
  });
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showFilters, setShowFilters] = useState(false);

  // عدّاد الفلاتر المتقدّمة المفعّلة
  const advancedCount = ['maxPrice', 'minPrice', 'minRooms', 'minArea', 'maxArea', 'q'].filter(
    (k) => filters[k as keyof typeof filters],
  ).length;

  const mapRef = useRef<HTMLDivElement>(null);
  const leafletMap = useRef<LMap | null>(null);
  const markersRef = useRef<Record<string, { marker: unknown; lat: number; lng: number }>>({});
  const [loaded, setLoaded] = useState(false);
  // 🗺️ حدود العرض الحالية (تتحدث مع الـ pan/zoom)
  const [viewBounds, setViewBounds] = useState<{ n: number; s: number; e: number; w: number } | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['properties-map', filters],
    queryFn: () => {
      // 🧹 احذف الفلاتر الفارغة وحوّل minRooms → rooms (اسم الـ API)
      const q: Record<string, string | number> = { status: 'ACTIVE', limit: 200, city: filters.city };
      if (filters.purpose)  q.purpose = filters.purpose.toUpperCase();
      if (filters.type)     q.type = filters.type;
      if (filters.minPrice) q.minPrice = filters.minPrice;
      if (filters.maxPrice) q.maxPrice = filters.maxPrice;
      if (filters.minArea)  q.minArea = filters.minArea;
      if (filters.maxArea)  q.maxArea = filters.maxArea;
      if (filters.minRooms) q.rooms = filters.minRooms;
      if (filters.q)        q.q = filters.q;
      return api.get<Paginated<PropMapItem>>('/api/properties', { query: q });
    },
  });

  // 🔨 المزادات النشطة (لها lat/lng من العقار المرتبط)
  const { data: aucData } = useQuery({
    queryKey: ['auctions-map', filters.city],
    queryFn: () => api.get<Paginated<{
      id: string; title: string; status: string; currentBid: number | string;
      property: { latitude: number | string | null; longitude: number | string | null; city: string; district: string | null } | null;
    }>>('/api/auctions', { query: { city: filters.city, limit: 100 } }),
  });

  // كل عقارات/مزادات المدينة (للرسم على الخريطة)
  const allProperties = (data?.data ?? []).filter((p) => p.latitude && p.longitude);
  const allAuctions = (aucData?.data ?? []).filter((a) =>
    ['SCHEDULED', 'LIVE'].includes(a.status) && a.property?.latitude && a.property?.longitude,
  );

  // 📐 فلترة بحسب الإطار الظاهر (لتحديث القائمة الجانبية والشارات)
  const inBounds = (lat: number, lng: number) => {
    if (!viewBounds) return true;
    return lat <= viewBounds.n && lat >= viewBounds.s && lng <= viewBounds.e && lng >= viewBounds.w;
  };
  const properties = allProperties.filter((p) => inBounds(Number(p.latitude), Number(p.longitude)));
  const auctions = allAuctions.filter((a) => inBounds(Number(a.property!.latitude), Number(a.property!.longitude)));

  const selected = properties.find((p) => p.id === selectedId);

  // 🚀 إعداد الخريطة
  useEffect(() => {
    let mounted = true;
    loadLeaflet().then((L) => {
      if (!mounted || !mapRef.current) return;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      delete (L.Icon.Default.prototype as any)._getIconUrl;
      L.Icon.Default.mergeOptions({
        iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
        iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
        shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
      });
      const start = CITY_CENTERS[filters.city] ?? CITY_CENTERS['الرياض'];
      const m = L.map(mapRef.current).setView(start, 12);
      // 🛰️ تصوير جوي
      L.tileLayer(
        'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
        { attribution: 'Tiles © Esri', maxZoom: 19 } as never,
      ).addTo(m);
      // ✏️ طبقة عناوين شفّافة لإظهار أسماء الشوارع/الأحياء
      L.tileLayer(
        'https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}',
        { attribution: '© Esri', maxZoom: 19, opacity: 0.85 } as never,
      ).addTo(m);
      leafletMap.current = m;
      // 📐 تتبّع حدود العرض كل ما يتحرك المستخدم
      const updateBounds = () => {
        const b = m.getBounds();
        setViewBounds({ n: b.getNorth(), s: b.getSouth(), e: b.getEast(), w: b.getWest() });
      };
      m.on('moveend', updateBounds);
      m.on('zoomend', updateBounds);
      // أول قراءة بعد التحميل
      setTimeout(updateBounds, 100);
      setLoaded(true);
    }).catch((e) => console.warn('Map load error', e));

    return () => {
      mounted = false;
      try { leafletMap.current?.remove(); } catch { /* ignore */ }
      leafletMap.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 🌍 إعادة التمركز حسب المدينة
  useEffect(() => {
    if (!leafletMap.current) return;
    const c = CITY_CENTERS[filters.city];
    if (c) leafletMap.current.setView(c, 12);
  }, [filters.city]);

  // 📍 رسم الـ markers لكل عقار
  useEffect(() => {
    if (!leafletMap.current || !window.L || !loaded) return;
    const L = window.L;
    // امسح القديم
    Object.values(markersRef.current).forEach((m) => {
      try { leafletMap.current!.removeLayer(m.marker); } catch { /* ignore */ }
    });
    markersRef.current = {};
    // 🏠 ماركرز العقارات — دبوس أنيق وواضح
    allProperties.forEach((p) => {
      const lat = Number(p.latitude); const lng = Number(p.longitude);
      if (!lat || !lng) return;
      const priceShort = compactPrice(Number(p.price));
      const color = p.purpose === 'sale' ? '#c8a456' : '#1e3a5f';
      const colorDark = p.purpose === 'sale' ? '#a8872f' : '#0d1f33';
      const typeEmoji = TYPE_EMOJI[p.type] ?? '🏠';
      const icon = L.divIcon({
        html: `<div style="position:relative;display:inline-flex;flex-direction:column;align-items:center;cursor:pointer;transition:transform 0.15s;" onmouseover="this.style.transform='scale(1.08)'" onmouseout="this.style.transform='scale(1)'">
          <div style="display:flex;align-items:center;gap:5px;background:${color};color:#fff;padding:6px 12px;border-radius:18px;font-weight:900;font-size:13px;font-family:Tajawal,sans-serif;white-space:nowrap;box-shadow:0 4px 12px rgba(0,0,0,0.35);border:2px solid white;line-height:1;">
            <span style="font-size:14px;">${typeEmoji}</span>
            <span>${priceShort}</span>
          </div>
          <div style="width:0;height:0;border-left:7px solid transparent;border-right:7px solid transparent;border-top:9px solid ${colorDark};margin-top:-1px;filter:drop-shadow(0 2px 2px rgba(0,0,0,0.25));"></div>
        </div>`,
        className: '',
        iconSize: [110, 38],
        iconAnchor: [55, 38],
      });
      const marker = L.marker([lat, lng], { icon });
      marker.bindPopup(`
        <div style="font-family:Tajawal,sans-serif;text-align:right;min-width:220px;padding:4px;">
          <div style="display:flex;align-items:center;gap:6px;margin-bottom:8px;">
            <span style="font-size:24px;">${typeEmoji}</span>
            <strong style="font-size:14px;color:#0d1f33;">${p.title}</strong>
          </div>
          <div style="color:#64748b;font-size:11px;margin-bottom:6px;">📍 ${p.district ?? p.city} · ${p.purpose === 'sale' ? 'للبيع' : 'للإيجار'}</div>
          <div style="color:#0d1f33;font-weight:900;font-size:16px;margin-bottom:8px;">${Number(p.price).toLocaleString('en-US')} <span style="font-size:11px;color:#64748b;font-weight:700;">ر.س</span></div>
          <a href="/properties/${p.id}" style="display:inline-block;padding:6px 14px;background:#c8a456;color:#0d1f33;text-decoration:none;border-radius:8px;font-weight:900;font-size:12px;">📋 التفاصيل ←</a>
        </div>
      `);
      marker.addTo(leafletMap.current!);
      marker.on('click', () => setSelectedId(p.id));
      markersRef.current[p.id] = { marker, lat, lng };
    });

    // 🔨 ماركرز المزادات — دبوس أحمر بارز
    allAuctions.forEach((a) => {
      const lat = Number(a.property!.latitude); const lng = Number(a.property!.longitude);
      if (!lat || !lng) return;
      const isLive = a.status === 'LIVE';
      const color = isLive ? '#dc2626' : '#ea580c';
      const colorDark = isLive ? '#991b1b' : '#9a3412';
      const icon = L.divIcon({
        html: `<div style="position:relative;display:inline-flex;flex-direction:column;align-items:center;cursor:pointer;transition:transform 0.15s;" onmouseover="this.style.transform='scale(1.08)'" onmouseout="this.style.transform='scale(1)'">
          <div style="display:flex;align-items:center;gap:5px;background:${color};color:#fff;padding:6px 12px;border-radius:18px;font-weight:900;font-size:12px;font-family:Tajawal,sans-serif;white-space:nowrap;box-shadow:0 4px 12px rgba(0,0,0,0.35);border:2px solid white;line-height:1;${isLive ? 'animation:pulse-marker 1.5s infinite;' : ''}">
            ${isLive ? '<span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:#fff;animation:blink 1s infinite;"></span>' : '<span style="font-size:14px;">🔨</span>'}
            <span>${isLive ? 'مباشر الآن' : 'مزاد قادم'}</span>
          </div>
          <div style="width:0;height:0;border-left:7px solid transparent;border-right:7px solid transparent;border-top:9px solid ${colorDark};margin-top:-1px;filter:drop-shadow(0 2px 2px rgba(0,0,0,0.25));"></div>
        </div>`,
        className: '',
        iconSize: [120, 38],
        iconAnchor: [60, 38],
      });
      const marker = L.marker([lat, lng], { icon });
      marker.bindPopup(`
        <div style="font-family:Tajawal,sans-serif;text-align:right;min-width:180px">
          <strong>${a.title}</strong><br/>
          <small style="color:#6b7280">📍 ${a.property?.district ?? a.property?.city ?? ''}</small><br/>
          <span style="font-weight:bold;color:${color}">${isLive ? '🔴 الآن — مباشر' : '🔨 مجدول'}</span><br/>
          <span style="color:#374151">المزايدة الحالية: <strong>${Number(a.currentBid).toLocaleString('en-US')} ر.س</strong></span>
          <br/><a href="/auctions/${a.id}" style="color:${color}">دخول الغرفة ←</a>
        </div>
      `);
      marker.addTo(leafletMap.current!);
      markersRef.current[`auction-${a.id}`] = { marker, lat, lng };
    });
  }, [allProperties, allAuctions, loaded]);

  // اختصار سعر بالإنجليزي (4K, 4.5K, 1M, 10M)
  function compactPrice(n: number): string {
    if (n >= 1_000_000) {
      const m = n / 1_000_000;
      const v = m % 1 === 0 ? m.toFixed(0) : m.toFixed(1).replace(/\.0$/, '');
      return `${v}M`;
    }
    if (n >= 1_000) {
      const k = n / 1_000;
      const v = k % 1 === 0 ? k.toFixed(0) : k.toFixed(1).replace(/\.0$/, '');
      return `${v}K`;
    }
    return n.toString();
  }


  // 🎯 ركّز على العقار المحدّد
  const focusProperty = (id: string) => {
    const m = markersRef.current[id];
    if (!m || !leafletMap.current) return;
    leafletMap.current.setView([m.lat, m.lng], 16);
    setSelectedId(id);
  };

  // 📍 موقعي الحالي
  const useMyLocation = () => {
    if (!navigator.geolocation) return alert('متصفحك لا يدعم تحديد الموقع');
    navigator.geolocation.getCurrentPosition(
      (pos) => leafletMap.current?.setView([pos.coords.latitude, pos.coords.longitude], 14),
      () => alert('تعذّر الحصول على موقعك'),
      { enableHighAccuracy: true },
    );
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <SEO
        title="خريطة العقارات والمزادات في السعودية 🗺️"
        description="استكشف كل العقارات والمزادات المتاحة في المملكة العربية السعودية على الخريطة. صور الأقمار الصناعية، فلاتر متقدّمة بالمدينة والنوع والسعر. اعثر على العقار المناسب لك جغرافياً."
        keywords="خريطة عقارات, خريطة العقار, خريطة المزادات, عقارات على الخريطة, عقارات الرياض على الخريطة, خريطة فلل, موقع العقار, خريطة العقارات السعودية, شركة صورة العقارية, img.sa"
        canonical="https://img.sa/map"
      />
      {/* 🎬 keyframes للنبض في ماركرز المزادات المباشرة */}
      <style>{`
        @keyframes pulse-marker {
          0%, 100% { box-shadow: 0 4px 12px rgba(220,38,38,0.4), 0 0 0 0 rgba(220,38,38,0.7); }
          50% { box-shadow: 0 4px 12px rgba(220,38,38,0.4), 0 0 0 10px rgba(220,38,38,0); }
        }
        @keyframes blink {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
      `}</style>

      <div className="text-center mb-6">
        <h1 className="text-3xl font-black text-navy-dark mb-2">🗺️ خريطة العقارات</h1>
        <p className="text-ink-secondary">استكشف العقارات على الخريطة بالموقع الفعلي</p>
      </div>

      {/* 🎯 شريط الفلاتر السريع — مثل التطبيقات */}
      <Card className="mb-4 overflow-hidden">
        <CardBody className="!p-3 space-y-3">
          {/* الصف الأول: للبيع/للإيجار + المدينة + زر فلتر متقدم */}
          <div className="flex items-center gap-2 flex-wrap">
            <div className="inline-flex bg-gray-100 rounded-xl p-1 gap-1">
              {[
                { v: '', label: 'الكل' },
                { v: 'sale', label: 'للبيع' },
                { v: 'rent', label: 'للإيجار' },
                { v: 'investment', label: 'استثمار' },
              ].map((opt) => (
                <button
                  key={opt.v}
                  onClick={() => setFilters({ ...filters, purpose: opt.v })}
                  className={`px-4 py-1.5 rounded-lg text-xs font-black transition ${
                    filters.purpose === opt.v
                      ? 'bg-navy-dark text-white shadow'
                      : 'text-ink-muted hover:text-navy-dark'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>

            <select
              value={filters.city}
              onChange={(e) => setFilters({ ...filters, city: e.target.value })}
              className="px-3 py-2 rounded-lg border-2 border-gray-200 bg-white text-sm font-bold focus:border-navy outline-none"
            >
              {SAUDI_CITIES.map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
            </select>

            <button
              onClick={() => setShowFilters(true)}
              className="ms-auto inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-navy-dark text-white text-xs font-black hover:bg-navy transition shadow"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="4" y1="21" x2="4" y2="14" /><line x1="4" y1="10" x2="4" y2="3" /><line x1="12" y1="21" x2="12" y2="12" /><line x1="12" y1="8" x2="12" y2="3" /><line x1="20" y1="21" x2="20" y2="16" /><line x1="20" y1="12" x2="20" y2="3" /><line x1="1" y1="14" x2="7" y2="14" /><line x1="9" y1="8" x2="15" y2="8" /><line x1="17" y1="16" x2="23" y2="16" />
              </svg>
              فلترة متقدمة
              {advancedCount > 0 && (
                <span className="bg-gold text-navy-dark rounded-full px-1.5 py-0.5 text-[10px] font-black min-w-5 text-center">
                  {advancedCount}
                </span>
              )}
            </button>
          </div>

          {/* الصف الثاني: شيبس النوع */}
          <div className="flex items-center gap-2 overflow-x-auto pb-1 -mx-1 px-1">
            {[
              { v: '',          label: 'الكل',     emoji: '🏘️' },
              { v: 'APARTMENT', label: 'شقة',      emoji: '🏢' },
              { v: 'VILLA',     label: 'فيلا',     emoji: '🏡' },
              { v: 'LAND',      label: 'أرض',      emoji: '🟫' },
              { v: 'COMMERCIAL', label: 'تجاري',   emoji: '🏬' },
              { v: 'OFFICE',    label: 'مكتب',     emoji: '🏢' },
              { v: 'BUILDING',  label: 'عمارة',    emoji: '🏗️' },
              { v: 'WAREHOUSE', label: 'مستودع',   emoji: '📦' },
              { v: 'FARM',      label: 'مزرعة',    emoji: '🌾' },
              { v: 'REST_HOUSE', label: 'استراحة', emoji: '🏖️' },
            ].map((opt) => {
              const active = filters.type === opt.v;
              return (
                <button
                  key={opt.v || 'all'}
                  onClick={() => setFilters({ ...filters, type: opt.v })}
                  className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold transition border-2 ${
                    active
                      ? 'bg-gold border-gold text-navy-darker'
                      : 'bg-white border-gray-200 text-ink-secondary hover:border-gold'
                  }`}
                >
                  <span>{opt.emoji}</span>
                  <span>{opt.label}</span>
                </button>
              );
            })}
          </div>
        </CardBody>
      </Card>

      {/* 🎛️ Drawer الفلترة المتقدّمة */}
      {showFilters && (
        <FilterDrawer
          filters={filters}
          setFilters={setFilters}
          onClose={() => setShowFilters(false)}
          onApply={() => setShowFilters(false)}
          onReset={() => {
            setFilters({ city: filters.city, purpose: '', type: '', maxPrice: '', minPrice: '', minRooms: '', minArea: '', maxArea: '', q: '' });
          }}
        />
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* الخريطة */}
        <div className="lg:col-span-2">
          <Card className="overflow-hidden">
            <div className="relative">
              <div ref={mapRef} className="w-full" style={{ height: '600px', background: '#e8eef5' }}>
                {!loaded && (
                  <div className="w-full h-full flex items-center justify-center text-ink-muted">⟳ جاري تحميل الخريطة…</div>
                )}
              </div>

              {/* 📍 شارة العقارات + المزادات في النطاق الجغرافي المحدد */}
              <div className="absolute top-4 right-4 bg-white/95 backdrop-blur rounded-2xl shadow-brand-lg px-4 py-2.5 z-[400] border border-navy/10">
                <div className="flex items-center gap-1 text-[10px] text-ink-muted font-bold tracking-wide uppercase">
                  <span>📍</span>
                  <span>{filters.city}</span>
                </div>
                <div className="flex items-center gap-3 mt-1">
                  <div className="flex items-baseline gap-1">
                    <span className="text-xl font-black text-gold">{properties.length}</span>
                    <span className="text-[10px] text-ink-secondary font-bold">عقار</span>
                  </div>
                  {auctions.length > 0 && (
                    <div className="flex items-baseline gap-1 border-r border-gray-200 pr-3">
                      <span className="text-xl font-black text-red-600">{auctions.length}</span>
                      <span className="text-[10px] text-red-700 font-bold">مزاد</span>
                    </div>
                  )}
                </div>
              </div>

              {/* 🎯 أزرار سريعة */}
              {loaded && (
                <div className="absolute bottom-4 left-4 z-[400] flex flex-col gap-2">
                  <button
                    onClick={useMyLocation}
                    title="موقعي الحالي"
                    className="w-11 h-11 rounded-full bg-white shadow-lg border-2 border-gray-300 hover:border-navy hover:bg-navy-soft transition flex items-center justify-center text-lg"
                  >
                    📍
                  </button>
                  {selected && (
                    <button
                      onClick={() => focusProperty(selected.id)}
                      title="ركّز على العقار المحدد"
                      className="w-11 h-11 rounded-full bg-navy-dark text-white shadow-lg border-2 border-navy hover:bg-navy transition flex items-center justify-center text-lg"
                    >
                      🎯
                    </button>
                  )}
                </div>
              )}
            </div>
          </Card>
        </div>

        {/* قائمة العقارات بجوار الخريطة */}
        <div className="lg:col-span-1">
          <Card className="h-full">
            <CardBody className="!p-3 max-h-[600px] overflow-y-auto">
              <div className="px-2 mb-3">
                <h3 className="font-black text-navy-dark text-sm">🏘️ العقارات في النطاق</h3>
                <p className="text-[10px] text-ink-muted mt-0.5">
                  {properties.length} {properties.length === 1 ? 'عقار' : 'عقاراً'} ضمن منطقة {filters.city}
                </p>
              </div>
              {isLoading ? (
                <div className="text-center py-8 text-ink-muted">⟳ جاري التحميل…</div>
              ) : properties.length === 0 ? (
                <div className="text-center py-8 text-ink-muted">
                  <div className="text-4xl mb-2">🏚️</div>
                  لا توجد عقارات بإحداثيات في {filters.city}
                </div>
              ) : (
                <div className="space-y-2">
                  {properties.map((p) => (
                    <div
                      key={p.id}
                      onMouseEnter={() => setSelectedId(p.id)}
                      onClick={() => focusProperty(p.id)}
                      className={`block p-3 rounded-xl border-2 transition cursor-pointer ${
                        selected?.id === p.id ? 'border-navy-dark bg-navy-soft' : 'border-gray-200 hover:border-navy hover:shadow-brand'
                      }`}
                    >
                      <div className="flex gap-3">
                        {p.coverImage ? (
                          <img src={p.coverImage} alt="" className="w-16 h-16 rounded-lg object-cover shrink-0" />
                        ) : (
                          <div className="w-16 h-16 rounded-lg bg-navy-soft flex items-center justify-center text-2xl shrink-0">🏠</div>
                        )}
                        <div className="flex-1 min-w-0">
                          <h4 className="font-bold text-sm line-clamp-1">{p.title}</h4>
                          <p className="text-xs text-ink-muted">{p.district ?? p.city}</p>
                          <div className="flex items-center justify-between mt-1">
                            <span className="font-black text-navy-dark text-sm">{Number(p.price).toLocaleString('en-US')} ر.س</span>
                            <Badge color={p.purpose === 'sale' ? 'gold' : 'navy'}>{p.purpose === 'sale' ? 'بيع' : 'إيجار'}</Badge>
                          </div>
                          <div className="flex gap-2 mt-2">
                            <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); focusProperty(p.id); }}>
                              🎯 على الخريطة
                            </Button>
                            <Link to={`/properties/${p.id}`} onClick={(e) => e.stopPropagation()}>
                              <Button size="sm" variant="ghost">عرض ←</Button>
                            </Link>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardBody>
          </Card>
        </div>
      </div>

      <div className="text-center text-xs text-ink-muted mt-4">
        💡 اضغط على أي علامة سعر في الخريطة لعرض تفاصيل العقار، أو على عقار من القائمة للتركيز عليه.
      </div>
    </div>
  );
}

// 🎛️ لوحة الفلترة المتقدّمة (Drawer من الجانب)
interface FiltersState {
  city: string; purpose: string; type: string;
  maxPrice: string; minPrice: string;
  minRooms: string; minArea: string; maxArea: string;
  q: string;
}
function FilterDrawer({
  filters, setFilters, onClose, onApply, onReset,
}: {
  filters: FiltersState;
  setFilters: (f: FiltersState) => void;
  onClose: () => void;
  onApply: () => void;
  onReset: () => void;
}) {
  return (
    <div className="fixed inset-0 z-[1000] flex">
      {/* خلفية معتمة */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />

      {/* اللوحة من اليمين */}
      <div className="relative ms-auto w-full sm:w-[420px] h-full bg-white shadow-2xl flex flex-col animate-in slide-in-from-right">
        <div className="flex items-center justify-between p-5 border-b">
          <h2 className="text-lg font-black text-navy-dark">🎛️ فلترة متقدّمة</h2>
          <button onClick={onClose} className="w-9 h-9 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-lg">✕</button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {/* 🔍 بحث نصّي */}
          <Section title="🔍 بحث">
            <input
              type="text"
              placeholder="ابحث في عنوان العقار…"
              value={filters.q}
              onChange={(e) => setFilters({ ...filters, q: e.target.value })}
              className="w-full px-3 py-2 rounded-lg border-2 border-gray-200 focus:border-navy outline-none text-sm"
            />
          </Section>

          {/* 💰 السعر */}
          <Section title="💰 نطاق السعر (ر.س)">
            <div className="grid grid-cols-2 gap-2">
              <input
                type="number" inputMode="numeric"
                placeholder="من"
                value={filters.minPrice}
                onChange={(e) => setFilters({ ...filters, minPrice: e.target.value })}
                className="w-full px-3 py-2 rounded-lg border-2 border-gray-200 focus:border-navy outline-none text-sm"
              />
              <input
                type="number" inputMode="numeric"
                placeholder="إلى"
                value={filters.maxPrice}
                onChange={(e) => setFilters({ ...filters, maxPrice: e.target.value })}
                className="w-full px-3 py-2 rounded-lg border-2 border-gray-200 focus:border-navy outline-none text-sm"
              />
            </div>
            {/* اختصارات */}
            <div className="flex flex-wrap gap-1.5 mt-2">
              {[100_000, 300_000, 500_000, 1_000_000, 3_000_000, 5_000_000].map((v) => (
                <button
                  key={v}
                  onClick={() => setFilters({ ...filters, maxPrice: String(v) })}
                  className="px-2.5 py-1 rounded-full bg-gray-100 hover:bg-gold/30 text-xs font-bold text-ink-secondary"
                >
                  حتى {v >= 1_000_000 ? `${v / 1_000_000} مليون` : `${v / 1_000} ألف`}
                </button>
              ))}
            </div>
          </Section>

          {/* 📐 المساحة */}
          <Section title="📐 المساحة (م²)">
            <div className="grid grid-cols-2 gap-2">
              <input
                type="number" inputMode="numeric"
                placeholder="من"
                value={filters.minArea}
                onChange={(e) => setFilters({ ...filters, minArea: e.target.value })}
                className="w-full px-3 py-2 rounded-lg border-2 border-gray-200 focus:border-navy outline-none text-sm"
              />
              <input
                type="number" inputMode="numeric"
                placeholder="إلى"
                value={filters.maxArea}
                onChange={(e) => setFilters({ ...filters, maxArea: e.target.value })}
                className="w-full px-3 py-2 rounded-lg border-2 border-gray-200 focus:border-navy outline-none text-sm"
              />
            </div>
          </Section>

          {/* 🚪 الغرف */}
          <Section title="🚪 عدد الغرف (الحد الأدنى)">
            <div className="flex flex-wrap gap-2">
              {['', '1', '2', '3', '4', '5', '6'].map((v) => {
                const active = filters.minRooms === v;
                return (
                  <button
                    key={v || 'any'}
                    onClick={() => setFilters({ ...filters, minRooms: v })}
                    className={`min-w-12 px-3 py-1.5 rounded-full text-sm font-bold transition border-2 ${
                      active ? 'bg-navy-dark border-navy-dark text-white' : 'bg-white border-gray-200 text-ink-secondary'
                    }`}
                  >
                    {v ? `${v}+` : 'الكل'}
                  </button>
                );
              })}
            </div>
          </Section>
        </div>

        {/* أزرار سفلية ثابتة */}
        <div className="border-t p-4 flex gap-2">
          <button
            onClick={onReset}
            className="flex-1 px-4 py-2.5 rounded-xl border-2 border-gray-200 text-ink-secondary font-black text-sm hover:bg-gray-50"
          >
            ↻ إعادة تعيين
          </button>
          <button
            onClick={onApply}
            className="flex-[2] px-4 py-2.5 rounded-xl bg-navy-dark hover:bg-navy text-white font-black text-sm shadow"
          >
            ✓ تطبيق الفلترة
          </button>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="font-black text-navy-dark text-sm mb-2">{title}</h3>
      {children}
    </div>
  );
}
